/* ============================================================
   FloatingDraggable — Apple AssistiveTouch-style floating bubble.

   Wraps any child node and makes it draggable around the viewport.
   On release it snaps to the nearest vertical edge (left or right)
   and remembers its position in localStorage so it stays where the
   customer left it across reloads.

   Owner ask Feb 2026: the customer-facing "Ring For Service" bell
   often sits exactly where the customer is trying to tap a
   modifier or scroll the menu. They asked for a draggable bell
   like the floating home button on Apple devices.

   Behavioural details:
     * Uses Pointer Events (works for mouse, touch, pen) with
       setPointerCapture so a drag started on the button keeps
       receiving events even if the finger drifts off it.
     * Distinguishes a TAP from a DRAG via a 6px movement
       threshold — a quick tap still fires the child's onClick
       (`ring the bell`); only a true drag suppresses the click.
     * Clamps movement inside the viewport with a 12px safe-area
       margin so the bubble can never get stuck off-screen.
     * Snap-to-edge: on release, the bubble moves to the closer
       vertical edge (left or right). Vertical position is kept
       exactly where the user dropped it.
     * Storage key namespaced per `storageKey` prop so different
       floating bubbles never collide. Quietly tolerates Safari
       private-mode (where localStorage throws).
   ============================================================ */
import React, { useEffect, useRef, useState, useCallback } from 'react';

const MOVE_THRESHOLD_PX = 6;
const EDGE_MARGIN_PX = 12;

export default function FloatingDraggable({
  storageKey,
  // Default placement: which edge to start at and how far from the
  // viewport top (bottom of viewport actually, because customer pages
  // have a sticky cart bar). Same shape we persist to localStorage.
  defaultPosition = { side: 'right', bottomPx: 80 },
  // Footprint of the wrapped content — used to clamp inside viewport.
  // Conservative defaults match the customer service bell.
  width = 88,
  height = 72,
  children,
  // Optional className applied to the wrapper for things like z-index
  // or a tiny rotate-on-hover. Position is controlled by the wrapper.
  className = '',
  'data-testid': dataTestId,
}) {
  const [pos, setPos] = useState(null);          // { leftPx, topPx } once measured
  const [isDragging, setIsDragging] = useState(false);
  const wrapperRef = useRef(null);
  const dragStartRef = useRef(null); // { pointerX, pointerY, originLeft, originTop, moved }
  const wasDraggingRef = useRef(false);

  // Convert a stored {side, topPx|bottomPx} to absolute pixel coords now.
  const resolveStored = useCallback((stored) => {
    if (!stored) return null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let leftPx;
    if (typeof stored.leftPx === 'number') {
      leftPx = stored.leftPx;
    } else if (stored.side === 'left') {
      leftPx = EDGE_MARGIN_PX;
    } else {
      leftPx = vw - width - EDGE_MARGIN_PX;
    }
    let topPx;
    if (typeof stored.topPx === 'number') {
      topPx = stored.topPx;
    } else if (typeof stored.bottomPx === 'number') {
      topPx = vh - height - stored.bottomPx;
    } else {
      topPx = vh - height - 80;
    }
    // Clamp inside the viewport with safe margins.
    leftPx = Math.max(EDGE_MARGIN_PX, Math.min(vw - width - EDGE_MARGIN_PX, leftPx));
    topPx = Math.max(64, Math.min(vh - height - 64, topPx));
    return { leftPx, topPx };
  }, [width, height]);

  // Hydrate initial position from localStorage (or defaults).
  useEffect(() => {
    let stored = null;
    try {
      const raw = storageKey ? localStorage.getItem(storageKey) : null;
      stored = raw ? JSON.parse(raw) : null;
    } catch { /* sandboxed / malformed — fall through to defaults */ }
    setPos(resolveStored(stored || defaultPosition));
  }, [storageKey, resolveStored, defaultPosition]);

  // Re-clamp on viewport resize / rotate so the bubble doesn't get
  // stranded off-screen when the device rotates.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => {
      setPos((p) => p ? resolveStored({ leftPx: p.leftPx, topPx: p.topPx }) : p);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pos, resolveStored]);

  const onPointerDown = (e) => {
    if (!pos) return;
    // Don't hijack non-primary buttons (right-click menus etc.).
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      originLeft: pos.leftPx,
      originTop: pos.topPx,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const onPointerMove = (e) => {
    const ds = dragStartRef.current;
    if (!ds) return;
    const dx = e.clientX - ds.pointerX;
    const dy = e.clientY - ds.pointerY;
    if (!ds.moved && Math.hypot(dx, dy) < MOVE_THRESHOLD_PX) return;
    ds.moved = true;
    if (!isDragging) setIsDragging(true);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const nextLeft = Math.max(EDGE_MARGIN_PX, Math.min(vw - width - EDGE_MARGIN_PX, ds.originLeft + dx));
    const nextTop = Math.max(64, Math.min(vh - height - 64, ds.originTop + dy));
    setPos({ leftPx: nextLeft, topPx: nextTop });
  };

  const onPointerUp = (e) => {
    const ds = dragStartRef.current;
    dragStartRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (!ds || !ds.moved) {
      setIsDragging(false);
      return; // pure tap — let the inner button handle its onClick
    }
    // Snap to the nearest vertical edge.
    const vw = window.innerWidth;
    const center = (pos?.leftPx ?? 0) + width / 2;
    const snappedLeft = center < vw / 2
      ? EDGE_MARGIN_PX
      : vw - width - EDGE_MARGIN_PX;
    const finalPos = { leftPx: snappedLeft, topPx: pos?.topPx ?? 0 };
    setPos(finalPos);
    try {
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(finalPos));
    } catch { /* sandboxed */ }
    wasDraggingRef.current = true;
    // Reset on next tick so the synthetic click event we WILL still
    // receive can be cancelled by `onClickCapture` below, then future
    // taps work normally.
    setTimeout(() => { wasDraggingRef.current = false; }, 0);
    setIsDragging(false);
  };

  // Swallow the click that follows a drag so the bell doesn't ring
  // when the customer is just repositioning it.
  const onClickCapture = (e) => {
    if (wasDraggingRef.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  if (!pos) {
    // First render — measure on next frame.
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
      data-testid={dataTestId}
      data-dragging={isDragging || undefined}
      style={{
        position: 'fixed',
        left: `${pos.leftPx}px`,
        top: `${pos.topPx}px`,
        touchAction: 'none', // prevent page-scroll while dragging
        transition: isDragging ? 'none' : 'left 220ms cubic-bezier(.22,.97,.36,1)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // Sit above the customer header (z-40) and the "place order" sticky
        // bar (z-[45]) so the bell stays visible no matter where the
        // customer drops it. Owner-reported Feb 2026: at the top of the
        // page it used to slide behind the sticky header.
        zIndex: 60,
      }}
      className={className}
    >
      {children}
    </div>
  );
}
